import { useCallback, useEffect, useState } from "react";
import { CalendarSync, RefreshCw, ShieldAlert, CheckCircle2, Video, Loader2 } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { fmtDateTime } from "../lib/format";

export default function OutlookCalendarCard() {
  const [st, setSt] = useState(null);
  const [log, setLog] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [mb, setMb] = useState({});

  const load = useCallback(async () => {
    try {
      const [{ data }, { data: lg }] = await Promise.all([api.get("/integrations/microsoft/calendar/status"), api.get("/integrations/microsoft/calendar/log", { params: { limit: 8 } })]);
      setSt(data); setLog(lg);
      setMb(Object.fromEntries(data.users.map((u) => [u.id, u.outlook_mailbox || ""])));
    } catch { setSt({ error: true }); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveSettings = async (patch) => {
    try { const { data } = await api.put("/integrations/microsoft/calendar/settings", patch); setSt((s) => ({ ...s, settings: data })); toast.success("Impostazioni calendario salvate"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); }
  };
  const saveUser = async (u, patch) => {
    try { await api.put(`/integrations/microsoft/calendar/mailboxes/${u.id}`, patch); toast.success(`Casella di ${u.name || u.email} aggiornata`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); }
  };
  const syncNow = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/integrations/microsoft/calendar/sync-now");
      if (data.ok) toast.success("Sincronizzazione completata"); else toast.error(data.reason === "disabled" ? "Sincronizzazione disattivata" : "Sincronizzazione già in corso");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); } finally { setSyncing(false); }
  };

  if (!st) return <div className="bg-white rounded-xl border border-slate-200 p-5 text-sm text-slate-400">Caricamento calendario Outlook…</div>;
  if (st.error) return null;
  const cfg = st.settings, perm = st.permission;
  const stats = st.last_stats;
  const sum = (o) => Object.values(o || {}).reduce((s, v) => s + (typeof v === "number" ? v : Object.values(v).reduce((a, b) => a + b, 0)), 0);

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="outlook-calendar-card">
      <div className="flex flex-wrap items-start justify-between gap-2 mb-1">
        <p className="text-sm font-heading font-semibold flex items-center gap-2"><CalendarSync className="w-4 h-4 text-teal-600" /> Calendario Outlook ↔ CRM</p>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500">Sincronizzazione</span>
          <Switch data-testid="outlook-enabled" checked={!!cfg.enabled} onCheckedChange={(v) => saveSettings({ enabled: v })} />
        </div>
      </div>
      <p className="text-sm text-slate-500 mb-3">Ogni attività CRM con data/ora diventa un evento nel calendario Outlook dell'owner; modifiche, spostamenti e cancellazioni fatte in Outlook tornano nel CRM. Gli eventi creati in Outlook vengono importati come attività "Meeting" (fonte Outlook).</p>

      <div data-testid="outlook-permission" className={`rounded-lg px-3 py-2.5 text-sm flex items-start gap-2 mb-3 ${perm.ok ? "bg-emerald-50 text-emerald-800 border border-emerald-200" : "bg-amber-50 text-amber-900 border border-amber-200"}`}>
        {perm.ok ? <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" /> : <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" />}
        <div className="min-w-0">
          {perm.ok ? <p>Permesso <code className="font-mono text-xs">Calendars.ReadWrite</code> attivo · calendario "{perm.calendar}" di {st.probe_mailbox}{perm.teams_available === false && " · Teams non disponibile su questa casella"}</p>
            : perm.code === "permission" ? <p><strong>Permesso mancante.</strong> In Entra ID → App registrations → API permissions aggiungi il permesso <strong>applicativo</strong> <code className="font-mono text-xs">Calendars.ReadWrite</code> e clicca "Grant admin consent". Poi ricarica questa pagina.</p>
            : <p><strong>{perm.code === "mailbox" ? "Casella non trovata" : "Verifica non riuscita"}</strong>{perm.error && <> · <span className="font-mono text-xs break-all">{perm.error}</span></>}</p>}
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-2 text-xs mb-3">
        <label className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 px-3 py-2"><span>Importa eventi Outlook nel CRM</span><Switch data-testid="outlook-import-external" checked={!!cfg.import_external} onCheckedChange={(v) => saveSettings({ import_external: v })} /></label>
        <label className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 px-3 py-2"><span className="flex items-center gap-1"><Video className="w-3.5 h-3.5" /> Link Teams per i Meeting</span><Switch data-testid="outlook-teams" checked={!!cfg.teams_for_meetings} onCheckedChange={(v) => saveSettings({ teams_for_meetings: v })} /></label>
        <label className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 px-3 py-2"><span>Intervallo (min)</span>
          <Input data-testid="outlook-interval" type="number" min={1} max={60} defaultValue={cfg.sync_interval_min} onBlur={(e) => Number(e.target.value) !== cfg.sync_interval_min && saveSettings({ sync_interval_min: Number(e.target.value) })} className="h-7 w-16 text-xs" /></label>
      </div>

      <p className="text-xs font-semibold text-slate-600 mb-1.5">Caselle sincronizzate (utente CRM → casella Microsoft 365)</p>
      <div className="divide-y divide-slate-100 border border-slate-100 rounded-lg mb-3">
        {st.users.map((u) => (
          <div key={u.id} data-testid={`outlook-user-${u.id}`} className="flex flex-wrap sm:flex-nowrap items-center gap-2 px-3 py-2">
            <div className="min-w-0 flex-1 basis-[60%] sm:basis-auto"><p className="text-sm font-medium text-slate-800 truncate">{u.name || u.email}</p><p className="text-[11px] text-slate-400 truncate">{u.email}</p></div>
            <Input data-testid={`outlook-mailbox-${u.id}`} value={mb[u.id] ?? ""} onChange={(e) => setMb((m) => ({ ...m, [u.id]: e.target.value }))} onBlur={() => (mb[u.id] || "") !== (u.outlook_mailbox || "") && saveUser(u, { mailbox: mb[u.id] })}
              placeholder={`${u.email} (predefinita)`} className="h-8 text-xs w-full sm:w-64" />
            <Switch data-testid={`outlook-user-sync-${u.id}`} checked={u.outlook_sync} onCheckedChange={(v) => saveUser(u, { enabled: v })} />
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500 mb-3">
        <Button data-testid="outlook-sync-now" onClick={syncNow} disabled={syncing || !cfg.enabled} size="sm" className="bg-navy-900 hover:bg-navy-700 text-white gap-1.5">{syncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Sincronizza ora</Button>
        <span data-testid="outlook-last-run">Ultima sincronizzazione: {st.last_run_at ? fmtDateTime(st.last_run_at) : "—"}{stats && ` · ${sum(stats.outbound)} CRM→Outlook · ${sum(stats.inbound)} Outlook→CRM`}</span>
        <span data-testid="outlook-linked">Collegati: {st.linked.crm} da CRM · {st.linked.outlook} da Outlook</span>
        {st.last_error && <span data-testid="outlook-last-error" className="text-red-600 font-medium truncate max-w-full" title={st.last_error}>Ultimo errore: {st.last_error}</span>}
      </div>

      {log.length > 0 && (
        <div data-testid="outlook-log" className="border border-slate-100 rounded-lg divide-y divide-slate-100 max-h-56 overflow-y-auto">
          {log.map((l) => (
            <div key={l.id} className="flex items-center gap-2 px-3 py-1.5 text-[11px]">
              <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${l.status === "ok" ? "bg-emerald-500" : "bg-red-500"}`} />
              <span className="font-mono text-slate-500 shrink-0">{l.direction === "outbound" ? "CRM→OL" : "OL→CRM"}</span>
              <span className="text-slate-700 shrink-0">{l.action}</span>
              <span className="text-slate-600 truncate flex-1">{l.subject || l.error || ""}</span>
              <span className="text-slate-400 shrink-0">{l.mailbox} · {fmtDateTime(l.created_at)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
